import { useEffect, useState, useMemo } from "react";
import ApiCustom from "../../utils/ApiCustom";
import {
  MagnifyingGlassIcon,
  ArrowsUpDownIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/solid";
import { formatCurrencyVND } from "../../utils/helps";
import ConfirmModal from "../../components/ConfirmModal.js";
import { useSearchParams, useNavigate } from "react-router-dom";
import useDebounce from "../../context/useDebounce.js";
import { useDispatch, useSelector } from "react-redux";
import { fetchCategoryByTypePublic } from "../../features/admin/CategorySlice.js";
import { fetchCompanies } from "../../features/admin/CompanySlice";
import axios from "axios";
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
  getPaginationRowModel,
} from "@tanstack/react-table";
import { Link } from "react-router-dom";
import { HomeIcon } from "@heroicons/react/24/outline";

const globalFilterFunction = (row, filterValue) => {
  if (!filterValue) return true;
  return ["name", "sku", "category"].some((key) =>
    row
      .getValue(key)
      ?.toString()
      .toLowerCase()
      .includes(filterValue.toLowerCase())
  );
};

export default function ProductList() {
  const [products, setProducts] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState(searchParams.get("s") || "");
  const debouncedSearch = useDebounce(searchTerm, 500);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 1000;
  const [isOpenModal, setIsOpenModal] = useState(false);
  const { categories } = useSelector((state) => state.categories);
  const { companies } = useSelector((state) => state.companies);
  const dispatch = useDispatch();
  const [categoryProducts, setCategoryProducts] = useState([]);
  const [sorting, setSorting] = useState([]);
  const [columnFilters, setColumnFilters] = useState([]);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [categoryCompanies, setCategoryCompanies] = useState("");

  useEffect(() => {
    getProducts();
  }, [debouncedSearch, page, categoryCompanies]);

  useEffect(() => {
    dispatch(fetchCategoryByTypePublic("company"));
    dispatch(fetchCompanies());
    getCategoryProduct();
  }, []);

  const getCategoryProduct = () => {
    axios
      .get(process.env.REACT_APP_API_URL + "api/categories/public")
      .then((res) => {
        setCategoryProducts(res.data.data.data);
      })
      .catch((err) => console.log(err));
  };

  const getProducts = () => {
    ApiCustom.get(
      `product-public-show?page=${page}&limit=${limit}&s=${searchTerm}&c=${categoryCompanies}&a=approved`
    )
      .then((res) => {
        setProducts(res.data.data.product);
        setTotalPages(res.data.data.totalPage);
      })
      .catch((err) => console.log(err));
  };

  const handleSearch = (e) => {
    setSearchTerm(e);
    setPage(1);
  };

  const handleCategoryChange = (e) => {
    setCategoryCompanies(e);
  };

  // const handlePageChange = (newPage) => {
  //   if (newPage >= 1 && newPage <= totalPages) {
  //     setPage(newPage);
  //   }
  // };

  const handleDetail = (product) => {
    if (product.madeBy === "KATEC") {
      navigate("/san-pham/" + product.slug);
    } else {
      setIsOpenModal(true);
    }
  };

  const columns = useMemo(
    () => [
      {
        header: "STT",
        accessorKey: "index",
        cell: ({ row }) => row.index + 1,
      },
      {
        header: "Hình ảnh",
        accessorKey: "urlImage",
        cell: (info) => {
          const image = info.getValue();
          return image ? (
            <img
              src={image}
              alt="Company Logo"
              className="w-14 h-14 object-cover"
              loading="lazy"
            />
          ) : (
            <div className="w-10 h-10 bg-gray-200 flex items-center justify-center text-gray-400 rounded-full">
              No img
            </div>
          );
        },
      },
      { header: "Tên sản phẩm", accessorKey: "name" },
      {
        id: "priceColumn",
        header: ({ column }) => (
          <div
            className="flex items-center gap-2 px-3 py-2 font-medium cursor-pointer rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Giá bán
            <ArrowsUpDownIcon className="h-5 w-5 " />
          </div>
        ),

        accessorFn: (row) => ({
          price: row.price,
          discountPrice: row.discountPrice,
        }),
        cell: ({ getValue }) => {
          const { price, discountPrice } = getValue();
          return (
            <div className="text-right">
              {formatCurrencyVND(
                discountPrice != 0 && discountPrice ? discountPrice : price
              )}
            </div>
          );
        },
      },
      {
        id: "discountPercentage",
        header: "Giảm giá",
        accessorFn: (row) => {
          const priceNum = Number(row.price);
          const discountNum = Number(row.discountPrice);

          if (
            priceNum == null ||
            discountNum == null ||
            discountNum === 0 ||
            priceNum <= discountNum
          ) {
            return 0;
          }
          return Math.round(((priceNum - discountNum) / priceNum) * 100);
        },
        cell: ({ getValue }) => {
          const discountPercent = getValue();
          return (
            <span className="font-bold">
              {discountPercent == 0 ? `` : discountPercent + `%`}
            </span>
          );
        },
      },
      { header: "Đơn vị cung cấp", accessorKey: "company.companyName" },
    ],
    []
  );

  const table = useReactTable({
    data: products,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    filterFns: {
      globalFilter: globalFilterFunction,
    },
    state: {
      sorting,
      columnFilters,
      pagination,
    },
  });

  const handleExportExcel = async () => {
    try {
      const response = await ApiCustom("/product-export-excel", {
        responseType: "blob",
      });

      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "danh_sach_san_pham.xlsx";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (error) {
      console.error("Lỗi khi tải file Excel:", error);
    }
  };

  return (
    <div className="container mx-auto">
      <nav className="flex text-gray-500 text-sm mb-4" aria-label="Breadcrumb">
        <ol className="inline-flex items-center space-x-1 md:space-x-2 flex-wrap">
          <li className="inline-flex items-center">
            <Link to="/" className="inline-flex items-center hover:text-blue-600 font-medium">
              <HomeIcon className="w-4 h-4 mr-2 text-gray-400" />
              Trang chủ
            </Link>
          </li>
          <li>
            <div className="flex items-center">
              <svg className="w-3 h-3 mx-2 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                <path d="M7.05 4.05a.75.75 0 011.06 0l5.25 5.25a.75.75 0 010 1.06l-5.25 5.25a.75.75 0 11-1.06-1.06L11.19 10 7.05 5.91a.75.75 0 010-1.06z" />
              </svg>
              <span className="ml-1 text-gray-400 font-medium">Sản phẩm</span>
            </div>
          </li>
        </ol>
      </nav>


      <div className="flex flex-col md:flex-row gap-4 items-center justify-between mb-4">
        <select
          className="w-full md:w-60 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
          onChange={(e) => handleCategoryChange(e.target.value)}
        >
          <option value="">Chọn Lĩnh vực</option>
          {categories.map((item, idx) => (
            <option key={idx} value={item.name}>
              {item.name}
            </option>
          ))}
        </select>
        <div className="flex items-center gap-4">
          <button
            className="px-2 py-1 btn btn-outline btn-success text-sm"
            onClick={handleExportExcel}
          >
            Xuất Excel
          </button>
          <div className="relative w-full md:w-64">
            <input
              type="text"
              placeholder="Tìm kiếm sản phẩm"
              className="w-full p-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
            />
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full border border-gray-300 shadow-lg rounded-lg">
          <thead className="bg-base-100 text-base-700">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th key={header.id} className="py-2 px-4 border">
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  className="hover:bg-base-200 transition cursor-pointer"
                  onClick={() => handleDetail(row.original)}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className="py-2 px-4 border">
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={columns.length}
                  className="hover:bg-base-200 transition cursor-pointer text-center p-2"
                >
                  Không có dữ liệu
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex flex-col gap-4 mt-4 md:flex-row md:items-center md:justify-between">
        <div className="flex flex-wrap gap-4 justify-center md:justify-start items-center">
          {/* Pagination Controls */}
          <div className="flex items-center gap-3 sm:gap-2">
            <button
              className="border p-2 rounded-md shadow-sm hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => table.firstPage()}
              disabled={!table.getCanPreviousPage()}
            >
              <ChevronDoubleLeftIcon className="w-4 h-4" />
            </button>
            <button
              className="border p-2 rounded-md shadow-sm hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              <ChevronLeftIcon className="w-4 h-4" />
            </button>
            <span className="text-sm sm:text-base font-medium">
              Trang <strong>{table.getState().pagination.pageIndex + 1}</strong>{" "}
              của {table.getPageCount().toLocaleString()}
            </span>
            <button
              className="border p-2 rounded-md shadow-sm hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              <ChevronRightIcon className="w-4 h-4" />
            </button>
            <button
              className="border p-2 rounded-md shadow-sm hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => table.lastPage()}
              disabled={!table.getCanNextPage()}
            >
              <ChevronDoubleRightIcon className="w-4 h-4" />
            </button>
          </div>

          {/* Go to Page */}
          <div className="flex items-center gap-1 sm:gap-2">
            <span className="hidden sm:block">Đi đến trang:</span>
            <input
              type="number"
              min="1"
              max={table.getPageCount()}
              defaultValue={table.getState().pagination.pageIndex + 1}
              onChange={(e) => {
                const page = e.target.value ? Number(e.target.value) - 1 : 0;
                table.setPageIndex(page);
              }}
              className="border border-gray-300 p-1 rounded-md w-12 sm:w-16 text-center outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-2 justify-center md:justify-end items-center">
          {/* Page Size Selector */}
          <div className="flex items-center gap-1 sm:gap-2">
            <span className="hidden sm:block">Hàng trên trang:</span>
            <select
              value={table.getState().pagination.pageSize}
              onChange={(e) => table.setPageSize(Number(e.target.value))}
              className="border border-gray-300 p-1 rounded-md outline-none focus:ring-2 focus:ring-blue-400"
            >
              {[5, 10, 20, 30, 40, 50].map((pageSize) => (
                <option key={pageSize} value={pageSize}>
                  {pageSize}
                </option>
              ))}
            </select>
          </div>

          {/* Total Rows Info */}
          <div className="text-sm sm:text-base">
            Hiển thị{" "}
            <strong>{table.getRowModel().rows.length.toLocaleString()}</strong>{" "}
            trên <strong>{table.getRowCount().toLocaleString()}</strong> dòng
          </div>
        </div>
      </div>

      <ConfirmModal
        isOpen={isOpenModal}
        onClose={() => setIsOpenModal(false)}
        message={"Liên hệ BCCU để được tư vấn.\n Sđt: 0915 776 663"}
      />
    </div>
  );
}
